import { z } from 'zod';
import { getMessageFromError } from '$lib/utils/error';
import * as Result from '$lib/utils/result';
import { safe } from '$lib/utils/string.js';
import { safeParse } from '$lib/utils/zod';

async function responseToResult<T>(
    response: Response,
    zData: z.ZodType<T>,
): Promise<Result.Result<T, Error>> {
    const zResponse = z.discriminatedUnion('status', [
        z.object({
            status: z.literal('success'),
            data: zData,
        }),
        z.object({
            status: z.literal('error'),
            message: z.string(),
        }),
    ]);

    const json = safeParse(zResponse, await response.json()) as Result.Result<
        { status: 'success'; data: T } | { status: 'error'; message: string },
        z.ZodError
    >;

    return Result.map(
        json,
        (json) => {
            if (json.status === 'success') {
                return Result.ok(json.data);
            } else {
                return Result.fail(new Error(json.message));
            }
        },
        (error) => {
            return Result.fail(new Error(error.message));
        },
    );
}

async function safeFetchAsJson<T>(
    url: string,
    requestInit: RequestInit,
    zData: z.ZodType<T>,
): Promise<Result.Result<T, Error>> {
    try {
        const response = await fetch(url, requestInit);
        return await responseToResult(response, zData);
    } catch (e) {
        return Result.fail(new Error(getMessageFromError(e)));
    }
}

export class UploadApi {
    constructor(private readonly baseUrl: string) {}

    async uploadMedia({
        file,
        context,
    }: {
        file: File;
        context: string;
    }): Promise<Result.Result<number, Error>> {
        const body = new FormData();
        body.append('file', file);
        body.append('context', context);

        const result = await safeFetchAsJson(
            safe`${this.baseUrl}/ajax/media/upload/image`,
            {
                method: 'POST',
                body,
            },
            z.object({ id: z.number() }),
        );

        return Result.mapOk(result, (data) => {
            return Result.ok(data.id);
        });
    }

    async uploadVideoLink({
        link,
        context,
    }: {
        link: string;
        context: string;
    }): Promise<Result.Result<number, Error>> {
        const body = new FormData();
        body.append('link', link);
        body.append('context', context);

        const result = await safeFetchAsJson(
            safe`${this.baseUrl}/ajax/media/upload/video/link`,
            {
                method: 'POST',
                body,
            },
            z.object({ id: z.number() }),
        );

        return Result.mapOk(result, (data) => {
            return Result.ok(data.id);
        });
    }

    async uploadFile({
        file,
        accepts,
    }: {
        file: File;
        accepts: readonly string[];
    }): Promise<Result.Result<number, Error>> {
        const body = new FormData();
        body.append('file', file);
        for (const accept of accepts) {
            body.append('accepts[]', accept);
        }

        const result = await safeFetchAsJson(
            safe`${this.baseUrl}/ajax/file/upload`,
            {
                method: 'POST',
                body,
            },
            z.object({ id: z.number() }),
        );

        return Result.mapOk(result, (data) => {
            return Result.ok(data.id);
        });
    }
}
